/**
 * KBCitationSidebar — right-rail Knowledge Base browser inside the lesson
 * editor. Click any doc to instantly append a Source blockquote to the
 * lesson body (`> Source: Title`) plus the machine-readable `[cite:doc-id]`
 * marker. Also lets admins "pin" one or more docs so the next AI
 * regeneration is forced to ground on them.
 *
 * Two power-user shortcuts live here:
 *   1. Bulk-cite — one click drops a "## Sources" list at the bottom of
 *      the lesson body containing every currently-pinned doc.
 *   2. Highlight-to-filter — when the parent passes a `highlight` string
 *      (text the admin has selected inside the lesson body), the sidebar
 *      asks the backend `/admin/ai-knowledge/search` endpoint to
 *      re-rank/filter docs so the closest matches float to the top.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../../App';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Library, Search, Pin, Quote, Loader2, Sparkles, ListChecks, Highlighter } from 'lucide-react';

const escapeHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const KBCitationSidebar = ({ description, onInsertToBody, pinnedIds, onTogglePin, onRegenerate, canRegenerate, highlight }) => {
  const { API } = useContext(AuthContext);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [regenBusy, setRegenBusy] = useState(false);
  const [highlightHits, setHighlightHits] = useState(null); // null = no highlight; [] = highlight but zero matches
  const [highlightBusy, setHighlightBusy] = useState(false);
  const highlightReqIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/admin/ai-knowledge/documents`)
      .then((r) => { if (!cancelled) setDocs(Array.isArray(r.data) ? r.data : (r.data?.documents || [])); })
      .catch(() => { if (!cancelled) setDocs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [API]);

  // Highlight-to-filter — debounced RAG search whenever the parent
  // passes a fresh highlight string (>3 chars). Cancels stale requests
  // via a monotonic request-id so out-of-order responses can't win.
  useEffect(() => {
    const text = (highlight || '').trim();
    if (text.length < 4) { setHighlightHits(null); setHighlightBusy(false); return; }
    const myId = ++highlightReqIdRef.current;
    setHighlightBusy(true);
    const t = setTimeout(() => {
      axios.post(`${API}/admin/ai-knowledge/search`, { query: text.slice(0, 2000), top_k: 20 })
        .then((r) => {
          if (highlightReqIdRef.current !== myId) return;
          setHighlightHits(Array.isArray(r.data) ? r.data : []);
        })
        .catch(() => { if (highlightReqIdRef.current === myId) setHighlightHits([]); })
        .finally(() => { if (highlightReqIdRef.current === myId) setHighlightBusy(false); });
    }, 400);
    return () => clearTimeout(t);
  }, [highlight, API]);

  // When a highlight-driven search is active, use its ranking; otherwise
  // fall back to the local title/filename/category filter on the full
  // library. Docs unknown to the search response are appended at the
  // end so pinned/other docs remain reachable.
  const visible = useMemo(() => {
    if (highlightHits) {
      const byId = new Map(docs.map((d) => [d.id, d]));
      const ranked = [];
      const seen = new Set();
      for (const h of highlightHits) {
        const d = byId.get(h.source_id);
        if (!d || seen.has(d.id)) continue;
        seen.add(d.id);
        ranked.push({ ...d, _matchScore: h.score, _matchSnippet: h.snippet });
      }
      return ranked;
    }
    if (!q.trim()) return docs;
    const needle = q.toLowerCase();
    return docs.filter((d) => `${d.title || ''} ${d.filename || ''} ${d.category || ''}`.toLowerCase().includes(needle));
  }, [docs, q, highlightHits]);

  const insertCitation = (d) => {
    const safeTitle = escapeHtml(d.title || d.filename || 'Untitled');
    const linkHtml = d.source_url
      ? `<a href="${escapeHtml(d.source_url)}" target="_blank" rel="noreferrer">${safeTitle}</a>`
      : safeTitle;
    const html = `<blockquote><p><strong>Source:</strong> ${linkHtml}</p></blockquote><p>[cite:${d.id}]</p>`;
    onInsertToBody((description || '') + html);
    toast.success(`Inserted "${d.title || d.filename}"`);
  };

  const bulkCitePinned = () => {
    const pinned = (docs || []).filter((d) => (pinnedIds || []).includes(d.id));
    if (pinned.length === 0) { toast.info('Pin at least one doc first'); return; }
    const items = pinned.map((d) => {
      const safeTitle = escapeHtml(d.title || d.filename || 'Untitled');
      const linkHtml = d.source_url
        ? `<a href="${escapeHtml(d.source_url)}" target="_blank" rel="noreferrer">${safeTitle}</a>`
        : safeTitle;
      return `<li>${linkHtml} <span>[cite:${d.id}]</span></li>`;
    }).join('');
    const block = `<h2>Sources</h2><ol>${items}</ol>`;
    // If a "## Sources" section already exists (from a prior bulk-cite),
    // replace it in-place so we don't accumulate duplicates every click.
    const current = description || '';
    const existingRe = /<h2>Sources<\/h2>\s*<ol>[\s\S]*?<\/ol>/i;
    const next = existingRe.test(current) ? current.replace(existingRe, block) : (current + block);
    onInsertToBody(next);
    toast.success(`Sources list updated with ${pinned.length} pinned doc${pinned.length === 1 ? '' : 's'}`);
  };

  const regenerate = async () => {
    if (!onRegenerate) return;
    setRegenBusy(true);
    try { await onRegenerate(); }
    finally { setRegenBusy(false); }
  };

  const pinnedCount = pinnedIds?.length || 0;
  const highlightActive = highlightHits !== null;
  const highlightPreview = (highlight || '').trim();

  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 p-3 flex flex-col h-full min-h-[420px]" data-testid="kb-citation-sidebar">
      <div className="flex items-center gap-2 mb-2">
        <Library className="w-4 h-4 text-primary" />
        <div className="font-heading font-extrabold text-sm">Knowledge Library</div>
        {pinnedCount > 0 && (
          <span className="ml-auto text-[10px] uppercase tracking-widest font-bold bg-primary text-primary-foreground rounded-full px-2 py-0.5" data-testid="kb-pinned-count">
            {pinnedCount} pinned
          </span>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">
        Click a doc to drop a <code>Source</code> blockquote + <code>[cite:id]</code> at the end of the lesson body. Pin one or more to force the next AI regeneration onto them.
      </p>

      <div className="relative mb-2">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search KB docs…"
          className="pl-7 h-8 text-xs"
          data-testid="kb-sidebar-search"
          disabled={highlightActive}
          title={highlightActive ? 'Clear the lesson-body highlight to search manually' : ''}
        />
      </div>

      {highlightActive && (
        <div className="mb-2 rounded-md border border-primary/40 bg-primary/10 px-2 py-1.5 text-[11px] text-primary flex items-start gap-1.5" data-testid="kb-highlight-banner">
          <Highlighter className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-bold uppercase tracking-widest text-[9px]">Filtering by highlight</div>
            <div className="truncate italic" title={highlightPreview}>"{highlightPreview.slice(0, 80)}{highlightPreview.length > 80 ? '…' : ''}"</div>
            {highlightBusy && <div className="text-muted-foreground">Searching KB…</div>}
            {!highlightBusy && highlightHits && highlightHits.length === 0 && <div className="text-muted-foreground">No matches — showing nothing. Clear the selection to browse.</div>}
          </div>
        </div>
      )}

      <div className="flex gap-1 mb-2">
        {canRegenerate && (
          <Button
            size="sm"
            onClick={regenerate}
            disabled={regenBusy || pinnedCount === 0}
            className="flex-1 rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            data-testid="kb-regenerate-pinned"
            title={pinnedCount === 0 ? 'Pin at least one doc first' : `Regenerate this lesson forcing the AI to cite ${pinnedCount} pinned source${pinnedCount === 1 ? '' : 's'}`}
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            {regenBusy ? 'Regenerating…' : `Regen (${pinnedCount})`}
          </Button>
        )}
        <Button
          size="sm"
          type="button"
          onClick={bulkCitePinned}
          disabled={pinnedCount === 0}
          variant="outline"
          className="rounded-full font-bold disabled:opacity-50"
          data-testid="kb-bulk-cite-pinned"
          title={pinnedCount === 0 ? 'Pin one or more docs first' : `Append a Sources list with all ${pinnedCount} pinned doc${pinnedCount === 1 ? '' : 's'} to the lesson body`}
        >
          <ListChecks className="w-3.5 h-3.5 mr-1" />
          Cite all
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto pr-1 space-y-1" data-testid="kb-sidebar-list">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="text-[11px] text-muted-foreground text-center py-6">
            {highlightActive ? 'No KB docs matched your highlight.' : 'No docs match.'}
          </div>
        ) : visible.map((d) => {
          const pinned = pinnedIds?.includes(d.id);
          return (
            <div key={d.id} className={`rounded-md border p-2 text-xs ${pinned ? 'border-primary bg-primary/10' : 'border-border bg-background/60'}`} data-testid={`kb-doc-${d.id}`}>
              <div className="font-bold text-foreground truncate" title={d.title || d.filename}>{d.title || d.filename}</div>
              {d.category && <div className="text-[10px] text-muted-foreground truncate">{d.category}</div>}
              {typeof d._matchScore === 'number' && (
                <div className="text-[10px] text-primary/80 mt-0.5" data-testid={`kb-match-score-${d.id}`}>
                  match {(d._matchScore * 100).toFixed(0)}%
                  {d._matchSnippet && <span className="text-muted-foreground"> — {d._matchSnippet.slice(0, 90)}{d._matchSnippet.length > 90 ? '…' : ''}</span>}
                </div>
              )}
              <div className="mt-1.5 flex gap-1">
                <button
                  type="button"
                  onClick={() => insertCitation(d)}
                  className="flex-1 inline-flex items-center justify-center gap-1 h-7 rounded-full px-2 text-[11px] font-bold bg-foreground text-background hover:bg-foreground/85"
                  data-testid={`kb-insert-${d.id}`}
                  title="Append Source blockquote + [cite:id] marker to lesson body"
                >
                  <Quote className="w-3 h-3" /> Insert
                </button>
                <button
                  type="button"
                  onClick={() => onTogglePin?.(d.id)}
                  className={`inline-flex items-center justify-center h-7 w-7 rounded-full ${pinned ? 'bg-primary text-primary-foreground' : 'border border-border text-muted-foreground hover:text-foreground'}`}
                  data-testid={`kb-pin-${d.id}`}
                  title={pinned ? 'Unpin from AI grounding' : 'Pin so AI regeneration cites this'}
                >
                  <Pin className="w-3 h-3" fill={pinned ? 'currentColor' : 'none'} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KBCitationSidebar;
